import { addHours, addMinutes, isAfter, min as minDate } from 'date-fns';

export const isRentalCard = (cardContentType: any): boolean =>
  [
    'reservedCar',
    'openedCar',
    'drivingCar',
    'parkingCar',
    'reservedOfflineService',
    'startedOfflineService',
    'mymTripStart',
    'mymTripStartLocation',
    'mymTripDestination',
    'mymTripOffer',
    'mymWaitingCar',
    'mymArrivedCar',
    'mymOpenedCar',
    'mymDrivingCar',
    'mymTransitCar',
    'mymExistCar',
  ].includes(cardContentType);

export const isCarSelectionMapType = (mapType: any): boolean =>
  [
    'availableCar',
    'selectedAvailableCar',
    'unavailableCar',
    'selectedUnavailableCar',
    'blockedCar',
  ].includes(mapType);

export const isRentalExpired = (rental: any | null | undefined): boolean => {
  if (rental && rental.access && rental.access.expires) {
    return isAfter(new Date(), new Date(rental.access.expires));
  }

  return false;
};

export const isRentalOngoing = (rental: any | null | undefined): boolean => {
  if (rental) {
    return (
      [
        'reserved',
        'opened',
        'driving',
        'parking',
        'offered',
        'readyToGo',
      ].includes(rental.carMode) && !isRentalExpired(rental)
    );
  }

  return false;
};

export const isRentalStarted = (rentalData: any | null | undefined): boolean =>
  ['opened', 'driving', 'parking'].includes(rentalData?.carMode);

export const isIgnitionStarted = (
  rentalData: any | null | undefined
): boolean => ['driving', 'parking'].includes(rentalData?.carMode);

export const isRentalMapContent = (
  mapContent: any | null | undefined
): boolean =>
  [
    'reservedCar',
    'openedCar',
    'drivingCar',
    'parkingCar',
    'rentalDriverCar',
    'rentalReturnLocation',
    'rentalNearestGasStation',
  ].includes(mapContent);

export const mapAnnotationFromCarMode = (
  carMode: any
): any | null | undefined => {
  switch (carMode) {
    case 'reserved':
      return 'reservedCar';

    case 'opened':
      return 'openedCar';

    case 'driving':
      return 'drivingCar';

    case 'parking':
      return 'parkingCar';

    case 'offered':
      return 'offeredCar';

    default:
      return null;
  }
};

export const mapCardFromCarMode = (
  carMode: any,
  isOfflineService: boolean = false
): any => {
  switch (carMode) {
    case 'reserved':
      return isOfflineService ? 'reservedOfflineService' : 'reservedCar';

    case 'opened':
      return isOfflineService ? 'startedOfflineService' : 'openedCar';

    case 'driving':
      return 'drivingCar';

    case 'parking':
      return 'parkingCar';

    case 'offered':
      return 'mymTripOffer';

    default:
      return 'none';
  }
};

/** Keyless vehicle detection. Backend OMITS keyFobRequired when a key fob IS
 *  required, so only an explicit `false` means keyless. Strict === false is mandatory. */
export const isKeylessRental = (rentalData: any | null | undefined): boolean =>
  rentalData?.carData?.keyFobRequired === false;

export const mymMapCardFromCarMode = (
  rentalData: any,
  prevCardType: string
): any => {
  const isTripStarted = !!rentalData.readyToGoSince;
  const atPickupPoint =
    rentalData.pickupStopName === rentalData.currentStopName;
  const atDropPoint = rentalData.releaseStopName === rentalData.currentStopName;
  console.log('*******************************');
  console.log('carMode', rentalData.carMode);
  console.log('pickupStopName', rentalData.pickupStopName);
  console.log('releaseStopName', rentalData.releaseStopName);
  console.log('currentStopName', rentalData.currentStopName);
  switch (rentalData.carMode) {
    case 'offered':
      return 'mymTripOffer';

    case 'reserved':
      return atPickupPoint ? 'mymArrivedCar' : 'mymWaitingCar';

    case 'opened':
      return 'mymOpenedCar';

    case 'readyToGo':
      return 'mymDrivingCar';

    case 'driving':
      return isTripStarted ? 'mymDrivingCar' : 'mymWaitingCar';

    case 'parking':
      return isTripStarted
        ? atDropPoint
          ? 'mymExistCar'
          : 'mymTransitCar'
        : 'mymArrivedCar';

    default:
      return prevCardType;
  }
};

export const FLEX_RENTAL_FEE_ITEM = 'F3037';
const INSTANT_RESERVATION_MINUTES = 30;
const FLEX_RESERVATION_MAX_HOURS = 24;

export const hasFlexOption = (
  rentalOrBooking: any | null | undefined
): boolean =>
  !!rentalOrBooking?.orderedOptionsWithCount?.some(
    (option: any) => option?.feeItem === FLEX_RENTAL_FEE_ITEM
  );

const getFlexDeadline = (
  isFlex: boolean,
  anchor: Date,
  bookedUntil: string | Date | null | undefined
): Date => {
  const instantDeadline = addMinutes(anchor, INSTANT_RESERVATION_MINUTES);
  if (!isFlex || !bookedUntil) return instantDeadline;

  const until = new Date(bookedUntil);
  if (Number.isNaN(until.getTime()) || !isAfter(until, anchor)) {
    return instantDeadline;
  }
  return minDate([until, addHours(anchor, FLEX_RESERVATION_MAX_HOURS)]);
};

export const getReservationExpiresAt = (
  rental: any | null | undefined,
  reservedAt: Date
): Date =>
  getFlexDeadline(hasFlexOption(rental), reservedAt, rental?.bookingTo);

export const getBookingNoLaterThan = (
  booking: any | null | undefined,
  fromDate: Date
): Date => getFlexDeadline(hasFlexOption(booking), fromDate, booking?.to);
